/**
 * Geography > Availability (WPS-222) - product availability across the whole
 * catalogue by zone / plant, with effective windows. The per-product view on
 * the product detail page reads the same rows; both invalidate each other.
 * Product codenames apply (this is catalogue data), not geo-zones.
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PackageCheck, Plus, Upload } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import type { ProductFilterFacet } from '@/shared/components/ui/product-filter-menu'
import { ResourceLookupContextProvider } from '@/shared/components/ui/resource-lookup-context'
import type { FormFieldMeta } from '@/shared/lib/forms'
import { toastApiError } from '@/shared/lib/api-error'
import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { EmptyState } from '@/admin/components/shared/empty-state'
import { EditFormDialog } from '@/admin/components/shared/edit-form-dialog'
import { ConfirmDeleteDialog } from '@/admin/components/confirm-delete-dialog'
import { BulkImportDialog } from '@/admin/components/imports/bulk-import-dialog'
import { usePermissions } from '@/admin/hooks/use-permissions'
import { useResourceLookupProviders } from '@/admin/hooks/use-resource-lookup-providers'
import { availability, useGeoMeta, type AvailabilityRow, useRefreshGeoData } from '@/admin/hooks/use-geo-reference'

import { CodeCell, GeographySectionShell, TextCell } from './geography-shared'
import { useCountLine, useZoneOptions, zoneLabel } from './geography-utils'
import { saveWithFieldErrors, useListState } from './section-crud'

export function AvailabilitySection() {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const canEdit = can('products.edit.global')

  const list = useListState()

  const refreshGeoData = useRefreshGeoData()
  const { data, isLoading, isFetching, isError, refetch } = availability.useList(list.params)
  const create = availability.useCreate()
  const update = availability.useUpdate()
  const remove = availability.useDelete()
  const { data: meta } = useGeoMeta()
  const zoneOptions = useZoneOptions()
  const lookupProviders = useResourceLookupProviders()

  const [editing, setEditing] = useState<AvailabilityRow | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [deleting, setDeleting] = useState<AvailabilityRow | null>(null)
  const [showImport, setShowImport] = useState(false)

  const rows = data?.results ?? []
  const countLine = useCountLine(
    data?.count,
    t('geography.availability.countOne', 'availability window'),
    t('geography.availability.countMany', 'availability windows'),
  )

  const facets = useMemo<ProductFilterFacet[]>(
    () => [
      { key: 'zone', label: t('geography.availability.field.zone', 'Zone'), kind: 'field', options: zoneOptions },
      {
        key: 'product_template',
        label: t('geography.availability.field.productTemplate', 'Product template'),
        kind: 'field',
        options: (meta?.product_templates ?? []).map((p) => ({ value: p.uuid, label: p.name })),
      },
    ],
    [meta?.product_templates, t, zoneOptions],
  )

  const fields = useMemo<FormFieldMeta[]>(
    () => [
      {
        code_name: 'product',
        display_name: t('geography.availability.field.product', 'Product'),
        // Live catalogue search, never a hardcoded list (thousands of products).
        field_type: 'ENTITY_SEARCH',
        required: true,
        configuration_data: { lookup_provider: 'product_model', icon: 'package' },
      },
      {
        code_name: 'zone_uuid',
        display_name: t('geography.availability.field.zone', 'Zone'),
        field_type: 'SELECT',
        required: true,
        options: zoneOptions,
        configuration_data: { searchable: true },
      },
      {
        code_name: 'effective_start',
        display_name: t('geography.availability.field.start', 'Available from'),
        field_type: 'DATE',
        required: true,
      },
      {
        code_name: 'effective_end',
        display_name: t('geography.availability.field.end', 'Available until'),
        field_type: 'DATE',
        required: false,
        description: t('geography.availability.field.endHint', 'Leave blank while the product stays available.'),
      },
    ],
    [t, zoneOptions],
  )

  const values = useMemo<Record<string, unknown>>(
    () =>
      editing
        ? {
            product: { uuid: editing.product_uuid, name: editing.product_name },
            zone_uuid: editing.zone_uuid,
            effective_start: editing.effective_start,
            effective_end: editing.effective_end ?? '',
          }
        : {},
    [editing],
  )

  const columns = useMemo<DataTableColumn<AvailabilityRow>[]>(
    () => [
      {
        id: 'product',
        header: t('geography.availability.column.product', 'Product'),
        sortable: true,
        sortField: 'product__cached_short_name',
        cell: (r) => (
          <div className="flex flex-col">
            <TextCell value={r.product_name} />
            {r.product_sku ? <CodeCell value={r.product_sku} /> : null}
          </div>
        ),
      },
      {
        id: 'product_template',
        header: t('geography.availability.column.template', 'Template'),
        cell: (r) => <TextCell muted value={r.product_template_name || '-'} />,
      },
      {
        id: 'zone',
        header: t('geography.availability.column.zone', 'Zone'),
        sortable: true,
        sortField: 'zone__name',
        cell: (r) => <TextCell value={zoneLabel(r.zone_name, r.zone_code)} />,
      },
      {
        id: 'window',
        header: t('geography.availability.column.window', 'Available'),
        sortable: true,
        sortField: 'effective_start',
        cell: (r) => (
          <TextCell
            muted
            value={`${r.effective_start} - ${r.effective_end ?? t('geography.availability.present', 'present')}`}
          />
        ),
      },
      {
        id: 'status',
        header: '',
        cell: (r) =>
          r.is_active ? (
            <Badge variant="secondary">{t('geography.availability.active', 'Active')}</Badge>
          ) : (
            <Badge variant="outline">{t('geography.availability.inactive', 'Inactive')}</Badge>
          ),
      },
      {
        id: 'actions',
        header: '',
        cell: (r) =>
          canEdit ? (
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="xs" onClick={() => { setEditing(r); setFormOpen(true) }}>
                {t('geography.common.edit', 'Edit')}
              </Button>
              <Button variant="ghost" size="xs" onClick={() => setDeleting(r)}>
                {t('geography.common.delete', 'Delete')}
              </Button>
            </div>
          ) : null,
      },
    ],
    [canEdit, t],
  )

  return (
    <>
      <GeographySectionShell
        testId="geography-availability"
        title={t('geography.availability.title', 'Availability')}
        description={t(
          'geography.availability.description',
          'Where and when each product is available, by plant service area or zone. Registration only offers products available at the address on the purchase date.',
        )}
        countLine={countLine}
        search={list.search}
        onSearchChange={list.setSearch}
        searchPlaceholder={t('geography.availability.searchPlaceholder', 'Search product, SKU or zone')}
        facets={facets}
        filterValues={list.filterValues}
        onFilterValuesChange={list.setFilterValues}
        actions={
          canEdit ? (
            <>
              <Button variant="outline" size="sm" onClick={() => setShowImport(true)}>
                <Upload className="h-3.5 w-3.5" />
                {t('geography.common.import', 'Import')}
              </Button>
              <Button
                size="sm"
                data-testid="geography-availability-add-btn"
                onClick={() => { setEditing(null); setFormOpen(true) }}
              >
                <Plus className="h-3.5 w-3.5" />
                {t('geography.availability.add', 'Add availability')}
              </Button>
            </>
          ) : null
        }
      >
        <DataTable<AvailabilityRow>
          tableId="geography-availability"
          columns={columns}
          data={rows}
          isLoading={isLoading}
          isFetching={isFetching}
          isError={isError}
          onRetry={() => refetch()}
          page={list.page}
          pageSize={list.pageSize}
          totalCount={data?.count ?? 0}
          onPageChange={list.setPage}
          onPageSizeChange={list.setPageSize}
          rowKey={(r) => r.uuid}
          emptyStateNode={
            <EmptyState
              icon={PackageCheck}
              title={t('geography.availability.emptyTitle', 'No availability windows')}
              description={t(
                'geography.availability.emptyDescription',
                'Declare where each product is available so registration can filter by location.',
              )}
            />
          }
        />
      </GeographySectionShell>

      <ResourceLookupContextProvider providers={lookupProviders}>
        <EditFormDialog
          open={formOpen}
          onOpenChange={(open) => { setFormOpen(open); if (!open) setEditing(null) }}
          title={
            editing
              ? t('geography.availability.editTitle', 'Edit availability')
              : t('geography.availability.addTitle', 'Add availability')
          }
          fields={fields}
          values={values}
          isPending={create.isPending || update.isPending}
          onSave={(v, _reason, helpers) =>
            saveWithFieldErrors(
              async () => {
                const product = v.product as { uuid?: string } | null
                const payload = {
                  product_uuid: product?.uuid ? String(product.uuid) : undefined,
                  zone_uuid: String(v.zone_uuid),
                  effective_start: String(v.effective_start),
                  effective_end: v.effective_end ? String(v.effective_end) : null,
                }
                if (editing) await update.mutateAsync({ uuid: editing.uuid, data: payload })
                else await create.mutateAsync(payload)
              },
              helpers,
              editing
                ? t('geography.availability.updated', 'Availability updated.')
                : t('geography.availability.created', 'Availability added.'),
              t('geography.availability.saveFailed', 'Could not save the availability.'),
            )
          }
        />
      </ResourceLookupContextProvider>

      <ConfirmDeleteDialog
        open={deleting !== null}
        onOpenChange={(open) => { if (!open) setDeleting(null) }}
        title={t('geography.availability.deleteTitle', 'Remove availability?')}
        description={
          deleting
            ? t(
                'geography.availability.deleteDescription',
                '{{product}} will no longer be offered in {{zone}}.',
                { product: deleting.product_name, zone: deleting.zone_name },
              )
            : undefined
        }
        isPending={remove.isPending}
        onConfirm={async () => {
          if (!deleting) return
          try {
            await remove.mutateAsync(deleting.uuid)
            toast.success(t('geography.availability.deleted', 'Availability removed.'))
            setDeleting(null)
          } catch (err) {
            toastApiError(err, t('geography.availability.deleteFailed', 'Could not remove the availability.'))
          }
        }}
      />

      <BulkImportDialog
        onComplete={refreshGeoData}
        open={showImport}
        onOpenChange={setShowImport}
        target="geo_product_availability"
        title={t('geography.availability.importTitle', 'Import availability')}
      />
    </>
  )
}
